import { rand, randNumber } from '@ngneat/falso';
import {
  HealthcareClaimStatus,
  InvoiceLineItemType,
  InvoiceStatus,
  RefundType,
} from '@principle-theorem/principle-core/interfaces';
import { TaxStrategy } from '@principle-theorem/accounting';
import {
  DimensionsCommon,
  IInvoiceDimension,
} from '@principle-theorem/reporting/interfaces';
import {
  getEnumValues,
  MockGenerator,
  MockTimestamp,
  toSerialisedTimestamp,
  toTimestamp,
} from '@principle-theorem/shared';
import { random } from 'lodash';
import { v4 as uuid } from 'uuid';
import {
  BigQueryFirestoreModelMock,
  BigQueryNamedDocumentMock,
  MockBigQueryStatusHistory,
  SerialisedDocumentReferenceMock,
} from './common.mock';

function MockTreatmentRef(): DimensionsCommon.ITreatmentRef {
  return {
    planRef: MockGenerator.generate(SerialisedDocumentReferenceMock),
    treatmentUuid: uuid(),
    attributedTo: MockGenerator.generate(BigQueryNamedDocumentMock),
  };
}

function MockCustomLineItem(
  type: InvoiceLineItemType
): DimensionsCommon.IBigQueryCustomLineItem {
  return {
    type,
    uid: uuid(),
    description: 'Mock line item',
    amount: randNumber({ min: 10, max: 500 }),
    taxStatus: TaxStrategy.GSTApplicable,
    tax: randNumber({ min: 0, max: 50 }),
    quantity: randNumber({ min: 1, max: 3 }),
  };
}

function MockServiceCodeLineItem(): DimensionsCommon.IBigQueryServiceCodeLineItem {
  return {
    ...MockCustomLineItem(InvoiceLineItemType.ServiceCode),
    code: String(randNumber({ min: 100, max: 999 })),
  };
}

class BigQueryTreatmentBaseLineItemMock
  extends BigQueryFirestoreModelMock<DimensionsCommon.ITreatmentBasePriceLineItem>
  implements DimensionsCommon.ITreatmentBasePriceLineItem
{
  type = InvoiceLineItemType.TreatmentBasePrice;
  uid = uuid();
  description = 'Mock treatment base price';
  amount = randNumber({ min: 10, max: 500 });
  taxStatus = TaxStrategy.GSTApplicable;
  tax = randNumber({ min: 0, max: 50 });
  quantity = 1;
  treatmentRef = MockTreatmentRef();
}

export class BigQueryTreatmentLineItemMock
  extends BigQueryFirestoreModelMock<DimensionsCommon.IBigQueryTreatmentLineItem>
  implements DimensionsCommon.IBigQueryTreatmentLineItem
{
  type = InvoiceLineItemType.Treatment;
  uid = uuid();
  description = 'Mock treatment line item';
  amount = randNumber({ min: 10, max: 500 });
  taxStatus = TaxStrategy.GSTApplicable;
  tax = randNumber({ min: 0, max: 50 });
  quantity = 1;
  items = {
    [InvoiceLineItemType.TreatmentBasePrice]: [
      MockGenerator.generate(BigQueryTreatmentBaseLineItemMock),
      MockGenerator.generate(BigQueryTreatmentBaseLineItemMock),
    ],
    [InvoiceLineItemType.ServiceCode]: [
      MockServiceCodeLineItem(),
      MockServiceCodeLineItem(),
    ],
  };
  treatmentRef = MockTreatmentRef();
}

export class BigQueryDepositLineItemMock
  extends BigQueryFirestoreModelMock<DimensionsCommon.IBigQueryDepositLineItem>
  implements DimensionsCommon.IBigQueryDepositLineItem
{
  type = InvoiceLineItemType.Deposit;
  uid = uuid();
  description = 'Mock deposit';
  amount = randNumber({ min: 10, max: 500 });
  taxStatus = TaxStrategy.GSTApplicable;
  tax = randNumber({ min: 0, max: 50 });
  quantity = 1;
  max = randNumber({ min: 100, max: 1000 });
  treatments = [MockGenerator.generate(BigQueryNamedDocumentMock)];
}

const lineItems: Partial<DimensionsCommon.IInvoiceDimensionItems> = {
  [InvoiceLineItemType.Product]: [
    MockCustomLineItem(InvoiceLineItemType.Product),
    MockCustomLineItem(InvoiceLineItemType.Product),
  ],
  [InvoiceLineItemType.Treatment]: [
    MockGenerator.generate(BigQueryTreatmentLineItemMock),
    MockGenerator.generate(BigQueryTreatmentLineItemMock),
  ],
  [InvoiceLineItemType.TreatmentBasePrice]: [
    MockGenerator.generate(BigQueryTreatmentBaseLineItemMock),
    MockGenerator.generate(BigQueryTreatmentBaseLineItemMock),
  ],
  [InvoiceLineItemType.ServiceCode]: [
    MockServiceCodeLineItem(),
    MockServiceCodeLineItem(),
  ],
  [InvoiceLineItemType.Deposit]: [
    MockGenerator.generate(BigQueryDepositLineItemMock),
    MockGenerator.generate(BigQueryDepositLineItemMock),
  ],
  [InvoiceLineItemType.Refund]: [
    {
      ...MockCustomLineItem(InvoiceLineItemType.Refund),
      refundType: RefundType.Card,
      sourceInvoice: MockGenerator.generate(SerialisedDocumentReferenceMock),
    },
  ],
  [InvoiceLineItemType.Fee]: [
    MockCustomLineItem(InvoiceLineItemType.Fee),
    MockCustomLineItem(InvoiceLineItemType.Fee),
  ],
};

export class InvoiceDimensionMock
  extends BigQueryFirestoreModelMock<IInvoiceDimension>
  implements IInvoiceDimension
{
  timestamp = toSerialisedTimestamp(toTimestamp());
  reference = uuid();
  status = rand([
    InvoiceStatus.Issued,
    InvoiceStatus.Issued,
    InvoiceStatus.Paid,
    InvoiceStatus.Paid,
    InvoiceStatus.Paid,
    InvoiceStatus.Paid,
    InvoiceStatus.Paid,
    ...getEnumValues(InvoiceStatus),
  ]);
  due = toSerialisedTimestamp(MockTimestamp());
  practice = MockGenerator.generate(BigQueryNamedDocumentMock);
  from = { name: 'Practice Name', address: '123 Main St' };
  to = { name: 'Patient Name', address: '456 Other St' };
  statusHistory = [MockBigQueryStatusHistory(InvoiceStatus.Draft)];
  items = lineItems;
  claims = [
    {
      uid: uuid(),
      status: HealthcareClaimStatus.Unclaimed,
      practitioner: MockGenerator.generate(BigQueryNamedDocumentMock),
      transactions: [MockGenerator.generate(SerialisedDocumentReferenceMock)],
      items: [
        {
          treatmentUid: uuid(),
          serviceCodeUid: uuid(),
          quantity: 1,
          provider: MockGenerator.generate(BigQueryNamedDocumentMock),
        },
      ],
    },
  ];
  subTotal = random(1, 60);
  tax = random(1, 6);
  total = random(1, 60);
}

export function MockCancelledInvoiceProps(): Pick<
  IInvoiceDimension,
  'status' | 'statusHistory' | 'cancelledAt' | 'cancellation'
> {
  return {
    status: InvoiceStatus.Cancelled,
    statusHistory: [
      MockBigQueryStatusHistory(InvoiceStatus.Draft),
      MockBigQueryStatusHistory(InvoiceStatus.Issued),
      MockBigQueryStatusHistory(InvoiceStatus.Cancelled),
    ],
    cancelledAt: toSerialisedTimestamp(MockTimestamp()),
    cancellation: {
      reason: 'Patient request',
      replacement: MockGenerator.generate(SerialisedDocumentReferenceMock),
    },
  };
}

export function MockPaidInvoiceProps(): Pick<
  IInvoiceDimension,
  'status' | 'statusHistory' | 'paidAt'
> {
  return {
    status: InvoiceStatus.Paid,
    statusHistory: [
      MockBigQueryStatusHistory(InvoiceStatus.Draft),
      MockBigQueryStatusHistory(InvoiceStatus.Issued),
      MockBigQueryStatusHistory(InvoiceStatus.Paid),
    ],
    paidAt: toSerialisedTimestamp(MockTimestamp()),
  };
}

export function MockIssuedInvoiceProps(): Pick<
  IInvoiceDimension,
  'status' | 'statusHistory'
> {
  return {
    status: InvoiceStatus.Paid,
    statusHistory: [
      MockBigQueryStatusHistory(InvoiceStatus.Draft),
      MockBigQueryStatusHistory(InvoiceStatus.Issued),
      MockBigQueryStatusHistory(InvoiceStatus.Paid),
    ],
  };
}
